import { Info } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Loader } from "@/components/common/loader";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { useFirmContext } from "@/features/clients/hooks/use-firm-context";
import {
  listNewArrivals,
  type NewArrival,
  type OnboardingStage,
  STAGE_LABELS,
} from "@/features/new-arrivals/api";
import { ManageOnboardingDialog } from "@/features/new-arrivals/components/manage-onboarding-dialog";

/** Stage filter options (plus an "all" sentinel). */
const STAGE_FILTERS: { value: "all" | OnboardingStage; label: string }[] = [
  { value: "all", label: "All stages" },
  ...(Object.keys(STAGE_LABELS) as OnboardingStage[]).map((stage) => ({
    value: stage,
    label: STAGE_LABELS[stage],
  })),
];

function stageBadgeVariant(stage: OnboardingStage): "default" | "secondary" | "destructive" {
  if (stage === "REJECTED") return "destructive";
  if (stage === "ONBOARDED" || stage === "APPROVED") return "default";
  return "secondary";
}

/**
 * The New Arrival queue: a card-wrapped table of clients in the onboarding
 * pipeline with an optional stage filter. GA/SA additionally see the owning
 * firm. "Manage" opens the pipeline dialog. Rejected rows surface the rework
 * reason inline.
 */
export function NewArrivalsQueue() {
  const user = useAuthStore((s) => s.user);
  const isPlatform = Boolean(user?.is_superuser || user?.is_platform_admin);
  // Scope to a BP only on an explicit drill-in (?firm=). Without one, GA/SA keep
  // the cross-firm approval queue (every org's arrivals) — unlike the Clients
  // page, this queue is not defaulted to the house book.
  const { actingFirmId } = useFirmContext();

  const [arrivals, setArrivals] = useState<NewArrival[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<"all" | OnboardingStage>("all");
  const [managing, setManaging] = useState<NewArrival | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /** Trigger a refetch (used after a transition closes the dialog). */
  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  // Fetch the queue whenever the stage filter changes or a refetch is requested.
  // The effect body initiates the request inline (setState lives in the async
  // callbacks), and `requestIdRef` discards out-of-order responses.
  useEffect(() => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    listNewArrivals({ firm: actingFirmId, ...(stage === "all" ? {} : { stage }) })
      .then((result) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setArrivals(result.arrivals);
      })
      .catch(() => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setError("Couldn't load new arrivals.");
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [stage, reloadKey, actingFirmId]);

  const colSpan = isPlatform ? 4 : 3;

  return (
    <>
      <div className="flex items-center justify-end">
        <Select value={stage} onValueChange={(v) => setStage(v as "all" | OnboardingStage)}>
          <SelectTrigger className="w-56" aria-label="Filter by stage">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STAGE_FILTERS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="gap-0 overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              {isPlatform && <TableHead>Firm</TableHead>}
              <TableHead>Stage</TableHead>
              <TableHead className="w-28 text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={colSpan}>
                  <div className="flex justify-center py-10">
                    <Loader />
                  </div>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={colSpan} className="text-destructive py-8 text-sm">
                  {error}
                </TableCell>
              </TableRow>
            ) : arrivals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} className="text-muted-foreground py-8 text-sm">
                  No new arrivals in this stage.
                </TableCell>
              </TableRow>
            ) : (
              arrivals.map((arrival) => (
                <TableRow key={arrival.id}>
                  <TableCell className="font-medium">{arrival.name}</TableCell>
                  {isPlatform && (
                    <TableCell className="text-muted-foreground">
                      {arrival.firmName ?? "—"}
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <Badge variant={stageBadgeVariant(arrival.onboardingStage)}>
                        {STAGE_LABELS[arrival.onboardingStage]}
                      </Badge>
                      {arrival.onboardingStage === "REJECTED" &&
                        arrival.onboardingRejectionReason && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`Rejection reason: ${arrival.onboardingRejectionReason}`}
                                  className="text-muted-foreground inline-flex"
                                >
                                  <Info className="size-4" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                {arrival.onboardingRejectionReason}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setManaging(arrival)}
                    >
                      Manage
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {managing && (
        <ManageOnboardingDialog
          arrival={managing}
          open={managing !== null}
          onOpenChange={(open) => {
            if (!open) setManaging(null);
          }}
          onTransitioned={refetch}
        />
      )}
    </>
  );
}
